/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import * as AppPropTypes from '../lib/PropTypes';
import useAuthLogin from '../hooks/useAuthLogin';
import useAuthLogout from '../hooks/useAuthLogout';
import useAuthCheck from '../hooks/useAuthCheck';

const AuthContext = React.createContext(null);

export const useAuth = () => useContext(AuthContext);

export const useUser = () => {
    const { user } = useAuth();
    return user;
};

export const useSetUser = () => {
    const { setUser } = useAuth();
    return setUser;
};

export const useLoggedIn = () => {
    const { loggedIn } = useAuth();
    return loggedIn;
};

const propTypes = {
    children: PropTypes.node.isRequired,
    user: AppPropTypes.user,
    checkOnMount: PropTypes.bool,
    checkInterval: PropTypes.number,
};

const defaultProps = {
    user: null,
    checkOnMount: false,
    checkInterval: 50,
};

export const AuthProvider = ({ user: initialUser, checkOnMount, checkInterval, children }) => {
    const [user, setUser] = useState(initialUser);
    const { login: authLogin } = useAuthLogin();
    const { logout: authLogout } = useAuthLogout();
    const { check: authCheck } = useAuthCheck();
    const [checkEnabled, setCheckEnabled] = useState(checkInterval !== null);

    const login = useCallback(
        (email, password) =>
            authLogin(email, password).then((newUser) => {
                setUser(newUser);
                return newUser;
            }),
        [authLogin, setUser],
    );
    const logout = useCallback(
        () =>
            authLogout().then(() => {
                setUser(null);
            }),
        [authLogout, setUser],
    );
    useEffect(() => {
        let canceled = false;
        if (checkOnMount && user !== null) {
            authCheck().then((newUser = null) => {
                if (canceled) {
                    return;
                }
                if (newUser !== null) {
                    setUser(newUser);
                }
            });
        }
        return () => {
            canceled = true;
        };
    }, [authCheck, checkOnMount, user]);

    useEffect(() => {
        let id = null;
        let canceled = false;
        if (user !== null && checkInterval !== null && checkEnabled) {
            id = setInterval(() => {
                authCheck()
                    .then((newUser = null) => {
                        if (canceled) {
                            return;
                        }
                        if (newUser !== null) {
                            setUser(newUser);
                        }
                    })
                    .catch(() => {
                        logout();
                    });
            }, checkInterval * 1000);
        }
        return () => {
            canceled = true;
            clearInterval(id);
        };
    }, [user, checkEnabled, authCheck, setUser, checkInterval, logout]);

    const onFocus = useCallback(() => {
        setCheckEnabled(true);
    }, [setCheckEnabled]);

    const onBlur = useCallback(() => {
        setCheckEnabled(false);
    }, [setCheckEnabled]);

    useEffect(() => {
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, [onFocus, onBlur]);

    const value = useMemo(
        () => ({ user, setUser, loggedIn: user !== null, logout, login }),
        [user, setUser, logout, login],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = propTypes;
AuthProvider.defaultProps = defaultProps;

export default AuthContext;
